/**
 * Canvas-level geometry: tiles, pixels, draw areas.
 *
 * The grid is the Web Mercator quadtree (see grid.ts): a pixel is one Mercator
 * pixel at TILE_ZOOM, a tile is 256 x 256 of them, and an activity cell is one
 * tile of the ACTIVITY_ZOOM grid (4 x 4 canvas tiles). Pixels are indexed
 * row-major within their tile, so a tile's byte array is already a raster.
 */
import {
  ACTIVITY_ZOOM,
  CELL_PREFIX,
  TILE_PIXELS,
  TILE_SIZE,
  TILE_ZOOM,
  WORLD_PIXELS,
  WORLD_TILES,
  distanceMeters,
  metresPerPixel,
  packToken,
  project,
  unpackToken,
  unproject,
  wrapX,
} from './grid.ts';

export { TILE_PIXELS, TILE_SIZE, TILE_ZOOM, ACTIVITY_ZOOM };

/** A canvas tile: one Web Mercator tile at TILE_ZOOM. */
export interface TileRef {
  x: number;
  y: number;
}

export interface PixelRef {
  tile: TileRef;
  /** Row-major index within the tile: py * 256 + px. */
  idx: number;
}

/** Numeric key for a tile, handy for Map caches. */
export function tileKey(t: TileRef): number {
  return t.y * WORLD_TILES + t.x;
}

export function tileFromKey(key: number): TileRef {
  return { x: key % WORLD_TILES, y: Math.floor(key / WORLD_TILES) };
}

export function tileToken(t: TileRef): string {
  return packToken(t.x, t.y, TILE_ZOOM, '');
}

export function tileFromToken(token: string): TileRef {
  return unpackToken(token, TILE_ZOOM, '');
}

export function isTileToken(token: string): boolean {
  try {
    tileFromToken(token);
    return true;
  } catch {
    return false;
  }
}

/** Activity cell token containing a tile (4 x 4 tiles). */
export function activityCellForTile(t: TileRef): string {
  return packToken(t.x >>> 2, t.y >>> 2, ACTIVITY_ZOOM, CELL_PREFIX);
}

export function activityCellFromToken(token: string): TileRef {
  return unpackToken(token, ACTIVITY_ZOOM, CELL_PREFIX);
}

/** Numeric key for an activity cell, for Map caches and the renderer. */
export function activityCellKey(c: TileRef): number {
  return c.y * (1 << ACTIVITY_ZOOM) + c.x;
}

export function activityCellFromKey(key: number): TileRef {
  const span = 1 << ACTIVITY_ZOOM;
  return { x: key % span, y: Math.floor(key / span) };
}

/** Centre of an activity cell given its grid position. */
export function activityCellRefCenter(c: TileRef): { lat: number; lng: number } {
  const scale = WORLD_PIXELS / (1 << ACTIVITY_ZOOM);
  return unproject((c.x + 0.5) * scale, (c.y + 0.5) * scale);
}

export function activityCellCenter(token: string): { lat: number; lng: number } {
  return tokenCenterLatLng(token);
}

/** Centre of whichever grid a token belongs to: a canvas tile or an activity cell. */
export function tokenCenterLatLng(token: string): { lat: number; lng: number } {
  if (token.startsWith(CELL_PREFIX)) {
    const c = activityCellFromToken(token);
    const scale = WORLD_PIXELS / (1 << ACTIVITY_ZOOM);
    return unproject((c.x + 0.5) * scale, (c.y + 0.5) * scale);
  }
  return tileCenterLatLng(tileFromToken(token));
}

// ---------------------------------------------------------------------------
// Pixels
// ---------------------------------------------------------------------------

export function pixelFromLatLng(lat: number, lng: number): PixelRef {
  const p = project(lat, lng);
  const gx = Math.min(WORLD_PIXELS - 1, Math.max(0, Math.floor(wrapX(p.gx))));
  const gy = Math.min(WORLD_PIXELS - 1, Math.max(0, Math.floor(p.gy)));
  return { tile: { x: Math.floor(gx / TILE_SIZE), y: Math.floor(gy / TILE_SIZE) }, idx: (gy % TILE_SIZE) * TILE_SIZE + (gx % TILE_SIZE) };
}

/** Local column/row of a pixel within its tile. */
export function idxToLocalIJ(_t: TileRef, idx: number): { li: number; lj: number } {
  return { li: idx % TILE_SIZE, lj: Math.floor(idx / TILE_SIZE) };
}

export function localIJToIdx(_t: TileRef, li: number, lj: number): number {
  return lj * TILE_SIZE + li;
}

/**
 * Table mapping local (li, lj) -> pixel index, entry `li * 256 + lj`. The grid
 * is row-major so this is a plain transpose, kept for callers that cache it.
 */
export function tileLocalIndexTable(_t: TileRef): Uint16Array {
  const table = new Uint16Array(TILE_PIXELS);
  for (let li = 0; li < TILE_SIZE; li++) for (let lj = 0; lj < TILE_SIZE; lj++) table[li * TILE_SIZE + lj] = lj * TILE_SIZE + li;
  return table;
}

/** Inverse table: pixel index -> packed (li << 8 | lj). */
export function tileLocalIJTable(_t: TileRef): Uint16Array {
  const table = new Uint16Array(TILE_PIXELS);
  for (let idx = 0; idx < TILE_PIXELS; idx++) table[idx] = ((idx % TILE_SIZE) << 8) | Math.floor(idx / TILE_SIZE);
  return table;
}

/** Global pixel coordinates of a pixel's top-left corner. */
export function pixelGlobal(t: TileRef, idx: number): { gx: number; gy: number } {
  return { gx: t.x * TILE_SIZE + (idx % TILE_SIZE), gy: t.y * TILE_SIZE + Math.floor(idx / TILE_SIZE) };
}

export function pixelCenterLatLng(t: TileRef, idx: number): { lat: number; lng: number } {
  const g = pixelGlobal(t, idx);
  return unproject(g.gx + 0.5, g.gy + 0.5);
}

export function pixelCornersLatLng(t: TileRef, idx: number): Array<{ lat: number; lng: number }> {
  const g = pixelGlobal(t, idx);
  return [unproject(g.gx, g.gy + 1), unproject(g.gx + 1, g.gy + 1), unproject(g.gx + 1, g.gy), unproject(g.gx, g.gy)];
}

export function tileCenterLatLng(t: TileRef): { lat: number; lng: number } {
  return unproject((t.x + 0.5) * TILE_SIZE, (t.y + 0.5) * TILE_SIZE);
}

export function tileCornersLatLng(t: TileRef): Array<{ lat: number; lng: number }> {
  const x0 = t.x * TILE_SIZE;
  const y0 = t.y * TILE_SIZE;
  const x1 = x0 + TILE_SIZE;
  const y1 = y0 + TILE_SIZE;
  return [unproject(x0, y1), unproject(x1, y1), unproject(x1, y0), unproject(x0, y0)];
}

/** Ground size of a pixel in metres. Constant across a tile to within a fraction of a percent. */
export function pixelSizeMeters(t: TileRef, idx: number): number {
  return metresPerPixel(pixelCenterLatLng(t, idx).lat);
}

/**
 * Ink multiplier for a tile: the ground area of its pixels relative to a pixel
 * at the equator. Mercator pixels shrink toward the poles, so charging ink by
 * this factor makes a given patch of the world cost the same everywhere.
 */
export function tileAreaFactor(t: TileRef): number {
  const c = Math.cos((tileCenterLatLng(t).lat * Math.PI) / 180);
  return c * c;
}

/** Chebyshev distance between two pixels of the same tile, in pixels. */
export function pixelChebyshevDistance(t: TileRef, a: number, b: number): number {
  const pa = idxToLocalIJ(t, a);
  const pb = idxToLocalIJ(t, b);
  return Math.max(Math.abs(pa.li - pb.li), Math.abs(pa.lj - pb.lj));
}

// ---------------------------------------------------------------------------
// Draw area
// ---------------------------------------------------------------------------

/**
 * Every pixel whose centre lies within `radiusM` of the position, grouped by
 * tile token. The grid is regular in Mercator, so the candidate box is exact
 * rather than sampled.
 */
export function drawArea(lat: number, lng: number, radiusM: number): Map<string, Uint16Array> {
  const centre = project(lat, lng);
  const reach = Math.ceil(radiusM / metresPerPixel(lat)) + 1;
  const gx0 = Math.floor(centre.gx) - reach;
  const gx1 = Math.floor(centre.gx) + reach;
  const gy0 = Math.max(0, Math.floor(centre.gy) - reach);
  const gy1 = Math.min(WORLD_PIXELS - 1, Math.floor(centre.gy) + reach);
  const byTile = new Map<number, { tile: TileRef; idxs: number[] }>();
  for (let gy = gy0; gy <= gy1; gy++) {
    for (let gxRaw = gx0; gxRaw <= gx1; gxRaw++) {
      const gx = wrapX(gxRaw);
      const c = unproject(gx + 0.5, gy + 0.5);
      if (distanceMeters(lat, lng, c.lat, c.lng) > radiusM) continue;
      const tile = { x: Math.floor(gx / TILE_SIZE), y: Math.floor(gy / TILE_SIZE) };
      const key = tileKey(tile);
      let entry = byTile.get(key);
      if (!entry) {
        entry = { tile, idxs: [] };
        byTile.set(key, entry);
      }
      entry.idxs.push((gy % TILE_SIZE) * TILE_SIZE + (gx % TILE_SIZE));
    }
  }
  const out = new Map<string, Uint16Array>();
  for (const { tile, idxs } of byTile.values()) {
    if (idxs.length) out.set(tileToken(tile), Uint16Array.from(idxs.sort((a, b) => a - b)));
  }
  return out;
}

/** Server-side check: is the pixel centre within radius (+ tolerance) of the position? */
export function pixelWithinRadius(t: TileRef, idx: number, lat: number, lng: number, radiusM: number, toleranceM = 1): boolean {
  const c = pixelCenterLatLng(t, idx);
  return distanceMeters(lat, lng, c.lat, c.lng) <= radiusM + toleranceM;
}

// ---------------------------------------------------------------------------
// Bounds
// ---------------------------------------------------------------------------

export interface LatLngBounds {
  south: number;
  west: number;
  north: number;
  east: number;
}

/** Grid positions of a bounding box at a given quadtree zoom. */
function boxAtZoom(b: LatLngBounds, zoom: number): { x0: number; x1: number; y0: number; y1: number } {
  const scale = (1 << zoom) / WORLD_PIXELS;
  const nw = project(b.north, b.west);
  const se = project(b.south, b.east);
  const span = 1 << zoom;
  const x0 = Math.floor(nw.gx * scale);
  const x1 = Math.floor(se.gx * scale);
  return {
    x0,
    // A box crossing the antimeridian wraps; walk forward from the west edge.
    x1: x1 >= x0 ? x1 : x1 + span,
    y0: Math.max(0, Math.floor(nw.gy * scale)),
    y1: Math.min(span - 1, Math.floor(se.gy * scale)),
  };
}

/** Tiles intersecting a lat/lng bounding box. */
export function tilesCoveringBounds(b: LatLngBounds): TileRef[] {
  const box = boxAtZoom(b, TILE_ZOOM);
  const out: TileRef[] = [];
  for (let y = box.y0; y <= box.y1; y++) {
    for (let x = box.x0; x <= box.x1; x++) out.push({ x: ((x % WORLD_TILES) + WORLD_TILES) % WORLD_TILES, y });
  }
  return out;
}

/** Activity cells intersecting a bounding box. */
export function activityCellsCoveringBounds(b: LatLngBounds): string[] {
  const span = 1 << ACTIVITY_ZOOM;
  const box = boxAtZoom(b, ACTIVITY_ZOOM);
  const out: string[] = [];
  for (let y = box.y0; y <= box.y1; y++) {
    for (let x = box.x0; x <= box.x1; x++) out.push(packToken(((x % span) + span) % span, y, ACTIVITY_ZOOM, CELL_PREFIX));
  }
  return out;
}
